import { Component, ElementRef, Input, OnInit, Output, ViewChild, EventEmitter, HostListener, ChangeDetectorRef, ChangeDetectionStrategy, AfterViewInit, OnDestroy } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { LayerService } from '../services/layer.service';
import { UserService } from '../services/user.service';
import ruleMap from "../add-layer/geo-css-ruleMap";
import { DisplayService } from '../services/display.service';
import * as loadingLogo from "../add-layer/LMI-loading-logo";
import { AppText, Languages, mapLayer } from '../interfaces/common';

@Component({
  selector: 'app-add-layer',
  templateUrl: './add-layer.component.html',
  styleUrls: ['./add-layer.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddLayerComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() applicationName: string = "";
  @Input() layerNameFromMainNav: string = "";
  @Input() selText: AppText = {};
  @Output() resetLayerName = new EventEmitter();
  @Output() reloadMapviewEE = new EventEmitter();
 
  layerForm: UntypedFormGroup = new UntypedFormGroup({});

  //Convention in Angular is to mark observables with dollar sign.
  layersObservable$: Observable<any> = of(null);

  stylesObservable$: Observable<any> = of(null);
  comboObservable$: Observable<[any, any]> = of([null, null]);
  comboSubscription: Subscription = new Subscription();

  //@ViewChild("listOfLayers") list: MatList;
  //The currently picked layer (won't change if user changes title is sort of the ID)
  pickedLayerName: string = "";

  //The current geometry type for the picked layer
  pickedLayerGeomType: string = "";
  //stylesForLayerGeomType array holds only style for value of pickedLayerGeomType. If pickedLayerGeomType is Line then it holds all line styles.
  stylesForLayerGeomType: object[] = [];

  //for removing highlight style when new layer is picked in the list
  oldLayerTarget: HTMLElement | null = null;

  command: string = "insert" //Possible commands for user layer, sent in the layerPackage to server. Possible values: insert | update | delete

  fillOpacity: number = 100;
  opacity: number = 100;

  dashArray: (number | null)[] = [null, null];

  userStyles: object[] = [];
  userLayers: object[] = [];

  showLayerPicker: boolean = true;

  statusMessageType: string = "Success";
  styleFormInitValues: UntypedFormGroup = new UntypedFormGroup({});
  activateStatusMessage: boolean = false;
  editMoreAvailable: boolean = false;
  loadingLogoIsHidden: boolean = false;

  layerNameForDeletion: string = "";

  //For mobile when in Layer manager.
  layerHamburgerMenuActive: boolean = false;

  loadingLogoCanvas: HTMLCanvasElement | null = null;
  // The use of '!' after the property declaration informs typescript that the property will be initialized before it is accessed
  @ViewChild("loadingLogoContainer", { static: true }) loadingLogoContainer!: ElementRef<any>;

  @HostListener('window:resize') resizeHandler(): void {
    if (window.innerWidth < 700 && this.pickedLayerName) {
      this.layerHamburgerMenuActive = true
    } else {
      this.layerHamburgerMenuActive = false
    }
  }

  constructor(private userService: UserService,
    private layerService: LayerService,
              private displayService: DisplayService,
              private changeDetector: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.initForm()

    if (this.layerNameFromMainNav !== undefined) {
      this.pickedLayerName = this.layerNameFromMainNav
      this.showLayerPicker = false;
      this.editMoreAvailable = false;
    }

    this.layersObservable$ = this.userService.listUserLayers();
    this.stylesObservable$ = this.userService.getUserStyles();
    this.comboObservable$ = combineLatest([this.layersObservable$, this.stylesObservable$])

    this.comboSubscription = this.comboObservable$.subscribe( ([layers, styles]) => {
      //console.log(layers)
      //console.log(styles)
      //Setting the styles
      this.userStyles = styles.styles
      // console.log("styles: ")
      // console.log(this.userStyles)

      //Setting the layers
      this.userLayers = layers.layers;
      // console.log("Layers")
      // console.log(this.userLayers)
      //Changing state and showing the user the layer manager
      this.loadingLogoIsHidden = true;
      this.changeDetector.markForCheck();
      loadingLogo.pauseAnimation();
      
      if (this.layerNameFromMainNav === undefined) {
        //console.log("User came from add-layer button")
        return
      }

      //If we have reached here then we want to load the layer that was clicked on in main-nav immideatly into view
      const findLayer = this.userLayers.find( (l: any) => l.table === this.layerNameFromMainNav)
      // Should maybe be like this to destroy 'any':
      //const findLayer = this.userLayers.find((l) => (l as { table: string }).table === this.layerNameFromMainNav);

      //console.log(findLayer)
  
      this.layerPicked(findLayer)
    })
    
    this.initEventListeners();

    //Creating and setting loadingLogo canvas
    //console.log(loadingLogo.getAnimID())
    this.loadingLogoCanvas = loadingLogo.getLoadingLogo(600, 600);
    //console.log(loadingLogo.getAnimID())
    this.loadingLogoCanvas.classList.add("loadingLogoCanvas")
    this.loadingLogoCanvas.style.width = "300px"
    this.loadingLogoCanvas.style.height = "300px"
    this.loadingLogoCanvas.style.zIndex = "3"

  }

  initEventListeners(): void {
    //Conversions for dashArray both for (form -> geoCSS) and (geoCSS -> form) 
    this.layerForm.get("style.dashVisible")?.valueChanges.subscribe( (newVal) => {
      //console.log(newVal);
      if (isNaN(newVal)) return
      newVal == false ? this.dashArray[0] = null : this.dashArray[0] = newVal 
    })
    this.layerForm.get("style.dashInvisible")?.valueChanges.subscribe( (newVal) => {
      //console.log(newVal);
      if (isNaN(newVal)) return
      newVal == false ? this.dashArray[1] = null : this.dashArray[1] = newVal
    })
    this.layerForm.get("style.dashFinal")?.valueChanges.subscribe( (newVal: any) => {
      if (newVal == false) return
      if (typeof(newVal) !== "string") return
      //if (newVal.includes("NaN")) return
      //console.log("New value of dashFinal: " + newVal + " type: " + typeof(newVal))
      const valueArray: string[] = newVal.split(" ")
      //console.log(valueArray)
      const visibleVal: number = parseInt(valueArray[0]);
      this.layerForm.get("style.dashVisible")?.setValue(visibleVal)
      this.dashArray[0] = visibleVal
      const invisibleVal: number = parseInt(valueArray[1]);
      this.layerForm.get("style.dashInvisible")?.setValue(invisibleVal)
      this.dashArray[1] = invisibleVal
    })
  }

  resetStyleSectionOnNewStyle(newValue: string) {
    if (newValue === "newStyle") {
      //console.log("User picked newStyle")
      this.layerForm.get("style")?.reset(this.styleFormInitValues)
      this.fillOpacity = 100
    }
    this.updateValidators(this.pickedLayerGeomType)
  }

  ngAfterViewInit(): void {
    loadingLogo.startAnimation()
    this.loadingLogoContainer.nativeElement.classList.add("loadingLogoContainer")
    this.loadingLogoContainer.nativeElement.appendChild(this.loadingLogoCanvas)
    
  }

  ngOnDestroy(): void {
    loadingLogo.pauseAnimation()
    this.comboSubscription.unsubscribe();
    this.resetLayerName.emit("")
  }

  initForm(): void {
    this.layerForm = new UntypedFormGroup({
      is: new UntypedFormControl("", []),
      en: new UntypedFormControl("", []),
      attributions: new UntypedFormControl("", []),
      metadata: new UntypedFormControl("", []),
      checked: new UntypedFormControl(true, []),
      opacity: new UntypedFormControl(1.0, []),
      stylesToPick: new UntypedFormControl("", []),
      style: new UntypedFormGroup({
        styleName: new UntypedFormControl("", [(control) => Validators.required(control)]),
        fillColor: new UntypedFormControl("", []),
        strokeColor: new UntypedFormControl("", []),
        symbol: new UntypedFormControl("", []),
        symbolSize: new UntypedFormControl("", []),
        strokeSize: new UntypedFormControl("", []),
        fillOpacity: new UntypedFormControl("", []),
        dashVisible: new UntypedFormControl("", []),
        dashInvisible: new UntypedFormControl("", []),
        dashFinal: new UntypedFormControl("", [])
      })
    })

    this.styleFormInitValues = this.layerForm.get("style")?.value;
  }

  layerPicked(pickedLayer: any, eventTarget: HTMLElement | null = null): void {
    //-----Styling------------
    if (eventTarget !== null) {
      const targetElement: HTMLElement = eventTarget;
      if (this.oldLayerTarget) {
        this.oldLayerTarget.removeAttribute("style");
      }
    
      targetElement.style.backgroundColor = "#cfcfcf";
      targetElement.style.border = "1px none"
      targetElement.style.borderRadius = "7px"
      this.oldLayerTarget = targetElement;
    }
    
    //-----Logic - setting correct state---------------
    if (window.innerWidth < 700) {
      this.layerHamburgerMenuActive = true;
    }

    //pickedLayer.table contains the current nam of layer/file
    this.pickedLayerName = pickedLayer.table;
    this.pickedLayerGeomType = this.determineGeometryType(pickedLayer.type);
    this.stylesForLayerGeomType = this.userStyles.filter( (style: any) => this.pickedLayerGeomType === this.determineStyleType(style.sld_body));
    //console.log("Type is: " + this.pickedLayerGeomType + "  All style of this type: ");
    //console.log(this.stylesForLayerGeomType);
    /*Reset style section of the form everytime layer is picked. This is done because if user is switching between layers
    with different geometryType then some leftovers of the previous geometry type will be in the css from constructGeoCSS()
    which results in an invalid geoCSS for this geometry type.*/
    this.layerForm.get("style")?.reset(this.styleFormInitValues)
    
    //console.log(`Layer: ${this.pickedLayerName} | Geometry type: ${this.pickedLayerGeomType}`)
    this.updateValidators(this.pickedLayerGeomType)
    this.populateLayerFields(pickedLayer)
  }

  /* Updates validators on the style form fields based on geometry type. 
  f.ex: type "line" then the user must enter a value for strokeColor and strokeSize */
  updateValidators(geometryType: string): void {
    const lf = this.layerForm as any
    const namesOfStyleControls = lf.get("style").controls
    //console.log("RUNNING AND SETTING VALIDATORS")
    //remove old validators and update validity
    for (const field in namesOfStyleControls) {
      this.layerForm.get(`style.${field}`)?.clearValidators()
      this.layerForm.get(`style.${field}`)?.updateValueAndValidity()
    }

    if (geometryType === "line") {
      this.layerForm.get("style.strokeColor")?.setValidators((control) => Validators.required(control));
      this.layerForm.get("style.strokeSize")?.setValidators((control) => Validators.required(control));
    }
    if (geometryType === "point") {
      this.layerForm.get("style.symbol")?.setValidators((control) => Validators.required(control));
      this.layerForm.get("style.symbolSize")?.setValidators((control) => Validators.required(control));
      this.layerForm.get("style.fillColor")?.setValidators((control) => Validators.required(control));
    }
    if (geometryType === "polygon") {
      this.layerForm.get("style.fillColor")?.setValidators((control) => Validators.required(control));
    }
    
    if (this.layerForm.get("stylesToPick")?.value == "newStyle") {
      this.layerForm.get("style.styleName")?.setValidators((control) => Validators.required(control));
    }
    

    for (const field in namesOfStyleControls) {
      this.layerForm.get(`style.${field}`)?.updateValueAndValidity()
    }
  }

  /* 
  If the user layer already exists in mapLayers then the user wants to edit a layer where he already some time in the past put in information 
  then I want to populate the fields with info from the layer so that user knows what his previous settings were. If this is not the case:
  populate with some default values.
  */
  populateLayerFields(pickedLayer: any): void {
    const layerName: string = pickedLayer.gsLayerName.split(":")[1]
    const layer: mapLayer | undefined = this.layerService.findLayerByName(layerName)
    if (layer) {
      this.determineCommand(layer)
    }

    if (this.command === "insert") {
      this.layerForm.reset()
      this.initForm()
      return 
    }

    this.layerForm.get("is")?.setValue(layer?.title_lang?.is || this.pickedLayerName)
    this.layerForm.get("en")?.setValue(layer?.title_lang?.en || this.pickedLayerName)
    this.layerForm.get("attributions")?.setValue(layer?.attributions || "")
    this.layerForm.get("metadata")?.setValue(layer?.metadata || "")
    this.layerForm.get("checked")?.setValue(layer?.checked || false) //Begin with false always
    this.layerForm.get("checked")?.markAsDirty()
    this.layerForm.get("stylesToPick")?.setValue(layer?.style || "")
    if (this.layerForm.get("stylesToPick")?.value !== "") {
      const styleObj: object | undefined = this.userStyles.find((style: any) => style.name === this.layerForm.get("stylesToPick")?.value);
      this.parseCSS(styleObj);
    }

    this.layerForm.get("opacity")?.setValue(layer?.option?.layer?.opacity || 1.0)
    this.opacity = layer?.option?.layer?.opacity * 100 || 100
      
    //default - No layer checking
    this.layerForm.get("style.styleName")?.setValue(this.pickedLayerName)
    this.layerForm.get("style.fillOpacity")?.setValue("")
    
  }

  determineCommand(layer: mapLayer): void {
    if (layer !== undefined) {
      this.command = "update"
    } else {
      this.command = "insert"
    }
  }

  handleSubmit(): void {
    //console.log("handling submit")
    //Need to mark all style inputs as touched. If not then the error hint doesnt show for required fields when user hits submit.
    //This is bad since user hits submit but nothing happens. The error hint is only activated if input is touched.
    this.layerForm.get("style")?.markAllAsTouched()
    //Donjt run invalid detection on style section if choosen style is "default"
    if (this.layerForm.get("stylesToPick")?.value !== "") {
      //User must enter a name for the style if creating a new style
      // console.log("StylesToPick is not default")
      // console.log(this.layerForm.get('style')?.invalid)
      // console.log(this.layerForm.get('style.styleName')?.invalid)
      if (this.layerForm.get('style.styleName')?.invalid || this.layerForm.get('style')?.invalid) {
        // console.log("Something is invalid...")
        // console.log(this.layerForm.get('style')?.invalid)
        // console.log(this.layerForm.get('style.styleName')?.invalid)
        return
      }
    }

    this.showLayerPicker = false;
    this.loadingLogoIsHidden = false;
    loadingLogo.startAnimation()
    //this.changeDetector.detectChanges();

    const changes: any[] = this.changeDetection()
    const geoCSS: string = this.constructGeoCSS();
    const readyLayerPackage: object = this.constructLayerPackage(geoCSS, changes);
    //console.log(this.pickedLayerName)
    this.sendLayerPackage(readyLayerPackage)
  }

  determineGeometryType(geomType: string): string {
    if (geomType.includes("POINT")) return "point"
    if (geomType.includes("LINE")) return "line"
    if (geomType.includes("POLYGON")) return "polygon"

    console.warn("Geometry type could not be determined! Defaulting to point")
    return "point"
  }

  /*
  I need to know if certain styles are for Points, polygons or lines. Only show Point styles
  if the pickedLayer that is being edited is of geometry type Point.
  Parsing the SLD body to determine this.
  */
  determineStyleType(SLDBody: string): string {
    if (SLDBody.includes("LineSymbolizer")) {
      //console.log("SLDBody incluse LineSymbolizer. Type is line")
      return "line";
    }
    else if (SLDBody.includes("PolygonSymbolizer")) {
      //console.log("SLDBody incluse PolygonSymbolizer. Type is polygon")
      return "polygon";
    }
    else if (SLDBody.includes("PointSymbolizer")) {
      //console.log("SLDBody incluse PointSymbolizer. Type is point")
      return "point";
    }
    else {
      return "unknown";
    }
  }

  /* Constructs a js object to be sent in the body of the POST request. Constructs layerPackages for "insert" and "update" commands
  OBS: for insert we need to send *all fields */
  constructLayerPackage(geoCSS: string, changes: any[]): object {
    //const [userLayerOrder, newDrawingLayerOrder] = this.getNewLayerOrder();
    const userLayerOrder = this.getNewLayerOrder();
    //console.log("userLayerOrder: " + userLayerOrder + "  " + "new layerOrder for drawingLayer: " + newDrawingLayerOrder)

    const layerPackage: any = {
      layer: {
        name: this.pickedLayerName,
        title: {is: this.layerForm.get("is")?.value, en: this.layerForm.get("en")?.value},
        checked: this.layerForm.get("checked")?.value,
        attributions: this.layerForm.get("attributions")?.value,
        metadata: this.layerForm.get("metadata")?.value,
        option: {opacity: this.layerForm.get("opacity")?.value},
        layermenu: 1,
        layerorder: userLayerOrder,
      },
      style: {
        name: this.layerForm.get("stylesToPick")?.value,
        body: geoCSS,
      },
      cmd: this.command,
      application: this.applicationName
    }

    const [styleChanges, layerChanges]: any[] = changes;

    //styleChanges is "pickedStyleWithChanges" if none of the 3 if statements here below are triggered
    if (styleChanges === null) {
      delete layerPackage.style
    }
    if (styleChanges === "pickedStyle") {
      delete layerPackage.style.body
    }
    if (styleChanges === "pickedStyleWithChanges") {
      //delete layerPackage.style.name
    }
    if (styleChanges === "newStyle") {
      layerPackage.style.name = this.layerForm.get("style.styleName")?.value
    }

    const layerPart = layerPackage.layer;

    const layerTraverser = (layerPart: { [key: string]: any }): object => {
      for (const layerProp in layerPart) {
        if (layerProp === "layerorder" && this.command === "insert") continue;
        if (layerProp === "layermenu" && this.command === "insert") continue;
        if (typeof(layerPart[layerProp]) === "object") {
          const nestedKeys: string[] = Object.keys(layerPart[layerProp])
          //console.log(nestedKeys)
          const goNested: boolean = nestedKeys.some( (key) => layerChanges.includes(key))
          //console.log(goNested)
          if (goNested === false) {
            delete layerPart[layerProp]
          } else {
            layerTraverser(layerPart[layerProp])
          }
          continue;
        }
        if (!layerChanges.includes(layerProp)) {
          //console.log("Deleting prop: " + layerProp)
          delete layerPart[layerProp]
        }
      }
      return layerPart
    }

    //Only apply layerDetection/removal of unchanged properties if command is update
    if (this.command === "update") {
      const updatedLayerPart: object = layerTraverser(layerPart)
      layerPackage.layer = updatedLayerPart;
    }
    //const updatedLayerPart: object = layerTraverser(layerPart)
    //layerPackage.layer = updatedLayerPart;
    return layerPackage;
  }

  sendLayerPackage(layerPackage: object): void {
    const postUserLayerChangesObs: Observable<any> = this.userService.postUserLayerChanges(layerPackage);

    postUserLayerChangesObs.subscribe(
      (response: any) => {
          if (response?.numberLayersInserted !== undefined || response?.deleted) {
            this.onSuccess()
          } else {
            this.onError()
          }   
      },
        (error) => {
          console.error(error)
          this.onError()
        } 
      )
  }

  /*
  Should start the process of events that happen after user clicks "submit".
  */
  onSuccess(): void {
    this.activateStatusMessage = true;
    this.loadingLogoIsHidden = true;
    loadingLogo.pauseAnimation()
    this.statusMessageType = "Success"

    setTimeout( () => {
      if (this.layerNameFromMainNav === undefined) {
        this.editMoreAvailable = true
      } 
      
      //this.displayService.toggleAddLayerActive()
      this.reloadMapviewEE.emit(true)
    }, 500)

  }

  onError(): void {
    this.activateStatusMessage = true;
    this.loadingLogoIsHidden = true;
    loadingLogo.pauseAnimation()
    this.statusMessageType = "Error"
  }


  /*
  For "update" command we must detect what has changed and what has not. [styleChanges, layerChanges].
  styleChanges possible values: null -> User wants to use defult style -> delete style object form layerPackage
                                "newStyle" -> Send everything
                                "pickedStyle" -> Send only the stylename (user wants to use pre-existing style)
                                "pickedStyleWithChanges" -> Send everything (user wants to use existing style and modify it)
  layerChanges possible values: [] -> No changes
                                [... some keys] -> list of form key names that have changed
  */
  changeDetection(): [string | null, string[]] {
    //StylePart
    let styleChangeValue: string | null = null;
    let layerChangeValue: string[] = [];

    styleChangeDetect: {
      if (this.layerForm.get("stylesToPick")?.value === "") {
        break styleChangeDetect;
      }
      if (this.layerForm.get("stylesToPick")?.value === "newStyle") {
        styleChangeValue = "newStyle";
        break styleChangeDetect;
      }

      const styleGroup: UntypedFormGroup = this.layerForm.get("style") as UntypedFormGroup
      //console.log(styleGroup.dirty)
      if (styleGroup.dirty) {
        styleChangeValue = "pickedStyleWithChanges"
        styleGroup.markAsPristine()
      } else {
        styleChangeValue = "pickedStyle"
      }
    }

    layerChangeDetect: {
      if (!this.layerForm.dirty) {
        //console.log("No changes made to layerPart")
        layerChangeValue = [];
        break layerChangeDetect;
      }

      //If we reached here then something has been changed in the layer info
      layerChangeValue = [];
      //const layerControls: any = this.layerForm.controls
      const layerControls: { [key: string]: AbstractControl<any> } = this.layerForm.controls;
      //console.log(layerControls)

      for (const key in layerControls) {
        //console.log(key + "  is dirty: " + layerControls[key].dirty)
        if (key === "stylesToPick" || key == "style") continue;
        if (layerControls[key].dirty) {
          layerChangeValue.push(key)
          //Ugly edge case fix, Need both "is" and "en" if only one of them was changed
          if (key === Languages.Icelandic) layerChangeValue.push(Languages.English)
          if (key === Languages.English) layerChangeValue.push(Languages.Icelandic)
        }
      }
    }

    //I want the name of the layer to always be in the layer part no matter what sort of changes/request I am making.
    layerChangeValue.push("name")

    //console.log([styleChangeValue, layerChangeValue])
    return [styleChangeValue, layerChangeValue]
  }

  getNewLayerOrderAndDrawingLayer(): number[] {
    const drawingLayer: mapLayer = this.layerService.getDrawingLayer()
    const userLayerOrder: number = drawingLayer.layerorder;
    const newDrawingLayerOrder: number = drawingLayer.layerorder + 1000;

    return [userLayerOrder, newDrawingLayerOrder]
  }

  getNewLayerOrder(): number {
    const drawingLayer: mapLayer = this.layerService.getDrawingLayer()
    const userLayerOrder: number = drawingLayer.layerorder;

    return userLayerOrder
  }

  //Should construct geoserver style CSS
  constructGeoCSS(): string {

    if (!this.pickedLayerGeomType) {
      console.warn("No geomtry type selected...Cannot construct CSS")
      return "";
    }

    //dashArray requires special treatment since its 2 values in form but combined to 1 in geoCSS.
    if (this.dashArray.every((val) => val !== null && (!isNaN(val)))) {
      this.layerForm.get("style.dashFinal")?.setValue(this.dashArray.join(" "))
    } else {
      this.layerForm.get("style.dashFinal")?.setValue("")
    }

    //val is array of strings, expression is stuff inside of ${...}
    const CSSValueValidator = (strings: TemplateStringsArray, expression: any) => {
      //console.log(typeof(strings))
      //console.log(strings)
      //console.log(expression)
      let output = ""
      
      if (expression === undefined || expression === null) return output
      const userValue: string = expression.toString()
      //If no value we want to completely erase this field, so just return empty string
      if (userValue === "") return output

      strings.forEach( (s: string) => {
        if (s.includes(":")) {
          output += s + userValue
          return 
        } 
        output += s;
      });

      return output
    }

    const GeoCSS: string = 
    ` * {
      ${CSSValueValidator`mark: symbol(${this.layerForm.get("style.symbol")?.value});`}
      ${CSSValueValidator`mark-size: ${this.layerForm.get("style.symbolSize")?.value};`}
      ${this.layerForm.get("style.symbol")?.value !== '' ? ":mark {" : ""}
      
      ${CSSValueValidator`stroke: ${this.layerForm.get("style.strokeColor")?.value};`}
      ${CSSValueValidator`stroke-width: ${this.layerForm.get("style.strokeSize")?.value}px;`}
      ${CSSValueValidator`fill: ${this.layerForm.get("style.fillColor")?.value};`}
      ${CSSValueValidator`fill-opacity: ${this.layerForm.get("style.fillOpacity")?.value};`}
      ${this.layerForm.get("style.symbol")?.value !== '' ? "}" : ""}
      ${CSSValueValidator`stroke-dasharray: ${this.layerForm.get("style.dashFinal")?.value};`}
    }`
    //console.log(GeoCSS)

    return GeoCSS;
  }

  parseCSS(singleStyle: any): void {
    // console.log("Parsing this style: ")
    // console.log(singleStyle)
    const CSSString = singleStyle.css_body
    //console.log(CSSString)
    const splittedCSS: string[] = CSSString.split(/\r?\n/).map((s: string) => s.trim()).filter( (s: string) => {
      if (s.length !== 0 && /[a-zA-Z]/.test(s) == true) {
        return s
      } 
    })
    //console.log(splittedCSS)
    
    const takeUntil = (valueArr: string[], accumulator: string, stop: string): string => {
      const char = valueArr.shift()
      if (char === stop || valueArr.length === 0) return accumulator
      accumulator += char
      return takeUntil(valueArr, accumulator, stop);
    }

    const takeUntilWithStart = (valueArr: string[], accumulator: string, start: string, stop: string, foundStart: boolean): string => {
      const char = valueArr.shift()
      if (char === stop) {
        //console.log("Return: + " + accumulator)
        return accumulator
      } 
      
      if (foundStart) {
        accumulator += char
      }

      if (char === start || valueArr.length === 0) {
        foundStart = true;
      }
      
      return takeUntilWithStart(valueArr, accumulator, start, stop, foundStart);
    }

    const CSSMap = new Map();

    //console.log(takeUntil([...y], "", "r"))
    splittedCSS.forEach(row => {
      const arrString: string[] = [...row];
      //console.log(arrString)
      //Note takeUntil will alter arrString (removed the elements it touched in the array)
      const ruleName: string = takeUntil(arrString, "", ":")
      //console.log(arrString)
      let ruleValue: string;
      if (arrString.includes("(")) {
        ruleValue = takeUntilWithStart(arrString, "", "(", ")", false)
      } else {
        ruleValue = takeUntil(arrString, "", ";")
      }
      
      ruleValue = ruleValue.trim()
      //Dont want to remove letters from hex color value
      if ((ruleValue.includes("px"))) ruleValue = ruleValue.replace(/[a-zA-Z]/g, "")
      //console.log("RuleName: " + ruleName + "   RuleValue: " + ruleValue)
      CSSMap.set(ruleName, ruleValue);
    });    
    //console.log(CSSMap)
    //console.log(ruleMap)

    //resetting style section before populating again
    this.layerForm.get("style")?.reset(this.styleFormInitValues)

    //Filling in style info in the form...
    for (const [key, value] of ruleMap) {
      //console.log(key + "  " + value)
      if (CSSMap.has(value)) {
        const ruleValue = CSSMap.get(value)
        //console.log("Setting layerForm " + key + " With value: " + ruleValue)
        //If its a color then I need to convert from hex to RGB for the color picker(it only accepts inputs as RGB)
        //if (ruleValue.includes("#")) ruleValue = this.hexToRGB(ruleValue)
        this.layerForm.get(`style.${key}`)?.setValue(ruleValue);
        //Special cases here below
        if (key === "fillOpacity") {
          this.fillOpacity = ruleValue * 100;
        } else {
          this.fillOpacity = 100
        }
      }
    }
  }

  setLayerOpacityValue(e: any): void {
    //console.log(e)
    const valueInPercent = e.value;
    const valueInCSS = valueInPercent / 100;
    this.layerForm.get("opacity")?.setValue(valueInCSS);
    this.layerForm.get("opacity")?.markAsDirty();
    //console.log(this.layerForm.get("opacity").value);
  }

  setFillOpacityValue(e: any): void {
    //console.log(e)
    const valueInPercent = e.value;
    const valueInCSS = valueInPercent / 100;
    this.layerForm.get("style.fillOpacity")?.setValue(valueInCSS);
    this.layerForm.get("style.fillOpacity")?.markAsDirty();
  }

  handleClosing() {
    this.displayService.toggleAddLayerActive();
  }

  openLayerEdit(refetchData: boolean) {
    // console.log("Received request to edit more layers in add-layer comp")
    // console.log(refetchData)
    this.activateStatusMessage = false;
    this.editMoreAvailable = false;
    if (this.layerNameFromMainNav === undefined) this.showLayerPicker = true;

    //console.log("pickedLayerName is: " + this.pickedLayerName + " GeomType of layer: " + this.pickedLayerGeomType)

    //dont refetch data if user went to delete and then clicked back
    if (refetchData === false) return
    
    //Get updated info from server, beacuse user changed something in some layer -> Update data in UI
    this.comboObservable$.subscribe( ([layers, styles]) => {
      //console.log(layers)
      this.userLayers = layers.layers
      //console.log(styles)
      this.userStyles = styles.styles

      if (this.pickedLayerName) {
        this.layerPicked(this.userLayers.find( (l: any) => l.table === this.pickedLayerName))
        // Should maybe be like this to destroy 'any':
        //this.layerPicked(this.userLayers.find((l) => (l as { table: string }).table === this.pickedLayerName))
      }
    })

    //reApply highlight style to list elment: (not working, this.oldLayerTarget is somehow not part of the DOM anymore)
    /*
    this.oldLayerTarget.style.backgroundColor = "#cfcfcf";
    this.oldLayerTarget.style.border = "1px none"
    this.oldLayerTarget.style.borderRadius = "7px"
    */
  }

  handleDeleteInit(layer: string) {
    //console.log("User requested a delete of the layer: " + layer);
    this.statusMessageType = "Delete";
    this.layerNameForDeletion = layer;
    this.activateStatusMessage = true;
  }

  

  handleDeleteRequest(deleteOptions: { file: boolean, layer: boolean, style: boolean }) {
    this.loadingLogoIsHidden = false;
    this.activateStatusMessage = false;

    //Construct delete object to send from the event:
    const deleteLayerPackage: { [key: string]: any } = {
      application: this.applicationName,
      cmd: "delete",
    }

    if (deleteOptions.file) deleteLayerPackage["data"] = [this.layerNameForDeletion]
    if (deleteOptions.layer) deleteLayerPackage["layers"] = [this.layerNameForDeletion] 
    if (deleteOptions.style) deleteLayerPackage["styles"] = [this.layerService.findLayerByName(this.layerNameForDeletion)?.style]
    
    // console.log("deleteRequestObject: ")
    // console.log(deleteLayerPackage)
    
    if (Object.values(deleteOptions).every( (d) => d === false)) {
      //console.log("Nothing picked for deletion, therefore not sending network request for delete")
      return;
    }

    this.sendLayerPackage(deleteLayerPackage)
  }

  toggleHamburgerMenu(): void {
    this.layerHamburgerMenuActive = !this.layerHamburgerMenuActive
    this.pickedLayerName = "";
  }
}